import { db } from '$db/local/db';
import type { OutboxOperation, QuarantineRecord } from '$db/local/db';
import { nowIso } from '$db/local/repositories/common';

/**
 * Utilidades para gestionar operaciones huérfanas de otras cuentas (M4.7).
 *
 * Cuando un usuario cambia de cuenta A → B en el mismo dispositivo, las
 * operaciones pendientes de A quedan huérfanas: siguen en la DB con
 * userId = A, pero RLS las rechazaría al intentar subirlas desde B.
 *
 * La app NUNCA las descarta automáticamente. Ofrece:
 *  - Exportarlas a JSON (backup del usuario).
 *  - Moverlas a la cuenta activa (con aviso de posibles duplicados).
 *  - Descartarlas (con confirmación explícita).
 *  - Dejarlas huérfanas (por si el usuario vuelve a A más tarde).
 */

export interface ForeignQueueSummary {
  userId: string;
  outboxCount: number;
  outboxBytes: number;
  quarantineCount: number;
  quarantineBytes: number;
}

/**
 * Resumen de la cola pendiente de un usuario concreto (incluido el activo).
 * Se usa al hacer signOut para avisar de las operaciones que quedarán
 * huérfanas.
 */
export async function getQueueSummaryForUser(
  userId: string
): Promise<ForeignQueueSummary> {
  const allOutbox = await db.outbox.where('userId').equals(userId).toArray();
  const allQuarantine = await db.quarantine
    .where('userId')
    .equals(userId)
    .toArray();

  const outboxCount = allOutbox.length;
  const outboxBytes = allOutbox.reduce((s, o) => s + (o.bytes ?? 0), 0);
  const activeQuarantine = allQuarantine.filter(
    (q) => q.status === 'quarantined'
  );
  const quarantineCount = activeQuarantine.length;
  const quarantineBytes = activeQuarantine.reduce(
    (s, q) => s + (q.bytes ?? 0),
    0
  );

  return {
    userId,
    outboxCount,
    outboxBytes,
    quarantineCount,
    quarantineBytes
  };
}

/**
 * Serializa un objeto a JSON y lo descarga como archivo en el navegador.
 * No-op si `document` no está disponible (SSR, tests).
 */
export function downloadJson(filename: string, data: unknown): void {
  if (typeof document === 'undefined') return;
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ForeignQueueExport {
  version: 1;
  exportedAt: string;
  sourceUserId: string;
  outbox: OutboxOperation[];
  quarantine: QuarantineRecord[];
}

/**
 * Lista todas las cuentas con operaciones huérfanas en este dispositivo.
 * Excluye al userId activo actual. El resultado está ordenado por userId
 * para estabilidad en tests y UI.
 */
export async function listForeignQueues(
  currentUserId: string
): Promise<ForeignQueueSummary[]> {
  const allOutbox = await db.outbox.toArray();
  const allQuarantine = await db.quarantine.toArray();

  const summary = new Map<string, ForeignQueueSummary>();

  function ensure(userId: string): ForeignQueueSummary {
    let entry = summary.get(userId);
    if (!entry) {
      entry = {
        userId,
        outboxCount: 0,
        outboxBytes: 0,
        quarantineCount: 0,
        quarantineBytes: 0
      };
      summary.set(userId, entry);
    }
    return entry;
  }

  for (const op of allOutbox) {
    if (op.userId === currentUserId) continue;
    const entry = ensure(op.userId);
    entry.outboxCount += 1;
    entry.outboxBytes += op.bytes ?? 0;
  }

  for (const rec of allQuarantine) {
    if (rec.userId === currentUserId) continue;
    if (rec.status !== 'quarantined') continue;
    const entry = ensure(rec.userId);
    entry.quarantineCount += 1;
    entry.quarantineBytes += rec.bytes ?? 0;
  }

  return Array.from(summary.values())
    .filter((e) => e.outboxCount > 0 || e.quarantineCount > 0)
    .sort((a, b) => a.userId.localeCompare(b.userId));
}

/**
 * Devuelve todas las ops + registros de cuarentena de una cuenta, listos
 * para serializar a JSON. No toca la DB.
 */
export async function exportForeignQueue(
  userId: string
): Promise<ForeignQueueExport> {
  const allOutbox = await db.outbox.toArray();
  const allQuarantine = await db.quarantine.toArray();

  return {
    version: 1,
    exportedAt: nowIso(),
    sourceUserId: userId,
    outbox: allOutbox
      .filter((op) => op.userId === userId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    quarantine: allQuarantine
      .filter((rec) => rec.userId === userId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  };
}

export interface MoveResult {
  outboxMoved: number;
  quarantineMoved: number;
}

/**
 * Mueve las operaciones huérfanas de `fromUserId` a `toUserId`.
 *
 * AVISO: si los datos ya existen en el servidor bajo `toUserId`, se
 * crearán duplicados lógicos (mismo contenido, IDs distintos). El
 * llamador debe advertir al usuario antes de invocar esto.
 *
 * También reescribe `userId` dentro del payload para que el RPC no
 * rechace la op por RLS al subirla desde la cuenta destino.
 */
export async function moveForeignQueueToUser(
  fromUserId: string,
  toUserId: string
): Promise<MoveResult> {
  if (fromUserId === toUserId) {
    return { outboxMoved: 0, quarantineMoved: 0 };
  }

  return db.transaction('rw', [db.outbox, db.quarantine], async () => {
    const outboxRows = await db.outbox
      .where('userId')
      .equals(fromUserId)
      .toArray();
    const quarantineRows = await db.quarantine
      .where('userId')
      .equals(fromUserId)
      .toArray();

    for (const op of outboxRows) {
      const patchedPayload = rewritePayloadUserId(op.payload, toUserId);
      await db.outbox.update(op.id, {
        userId: toUserId,
        payload: patchedPayload
      });
    }

    for (const rec of quarantineRows) {
      const patchedPayload = rewritePayloadUserId(rec.payload, toUserId);
      await db.quarantine.update(rec.id, {
        userId: toUserId,
        payload: patchedPayload
      });
    }

    return {
      outboxMoved: outboxRows.length,
      quarantineMoved: quarantineRows.length
    };
  });
}

function rewritePayloadUserId(payload: unknown, toUserId: string): unknown {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload;
  }
  const obj = payload as Record<string, unknown>;
  if (!('userId' in obj)) return payload;
  return { ...obj, userId: toUserId };
}

export interface DiscardResult {
  outboxDiscarded: number;
  quarantineDiscarded: number;
}

/**
 * Borra físicamente todas las ops y registros de cuarentena de `userId`.
 * Acción destructiva: el llamador debe confirmar con el usuario.
 */
export async function discardForeignQueue(
  userId: string
): Promise<DiscardResult> {
  return db.transaction('rw', [db.outbox, db.quarantine], async () => {
    const outboxRows = await db.outbox
      .where('userId')
      .equals(userId)
      .toArray();
    const quarantineRows = await db.quarantine
      .where('userId')
      .equals(userId)
      .toArray();

    if (outboxRows.length > 0) {
      await db.outbox.bulkDelete(outboxRows.map((o) => o.id));
    }
    if (quarantineRows.length > 0) {
      await db.quarantine.bulkDelete(quarantineRows.map((q) => q.id));
    }

    return {
      outboxDiscarded: outboxRows.length,
      quarantineDiscarded: quarantineRows.length
    };
  });
}